import { Link, PrimaryButton } from "@certn/cascadia";
import { Card, Divider, Stack, Text } from "@certn/cascadia-react";
import { formatCurrency, slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { Trans, useTranslation } from "react-i18next";

import { PASS_THROUGH_ACCESS_FEES_URL } from "../../constants";
import { Order, SelectedChecksType } from "../../pages/order/OrderContext";
import { getOrderCost } from "../../pages/order/order-utils";
import { useOrderBillingProfile } from "../../pages/order/steps/useOrderBillingProfile";
import { Schemas } from "../../types";
import { SummaryItemWithChecks } from "../order-summary/components/SummaryItemWithChecks";
import { useCanViewClientPortalPricing } from "../permissions/Permissions";
import { useFlags } from "launchdarkly-react-client-sdk";
import { formatNullableCurrency } from "../../utils/formatNullableCurrency";
import { getSelectedUmbrellaOptions } from "../../utils/getSelectedUmbrellaOptions";
import { AddOnOrderSummaryContainer } from "../ordering-check-card/components/AddOnOrderSummaryContainer";

interface OrderReviewSummaryBoxProps {
  order: Order;
  isSubmitting?: boolean;
  border?: "dropshadow" | "none";
  hideBorder?: boolean;
  withoutPriceInfo?: boolean;
  onPlaceOrder?(): void;
  disablePlaceOrder?: boolean;
  region?: string;
  isQuickscreen?: boolean;
}

// DAT-1614: payment-profile-presence-only check by design. Inactive cards are rare
// and the backend returns a clear 400, so we don't expose `payment_profile.status`
// in the schema. Revisit if inactive-card UX becomes a real complaint.
export function checkIfReadyToOrder(
  profile?: Schemas["CustomerGroupBillingProfile"],
  cardOnFileFlag = false
) {
  if (!profile) {
    return false;
  }

  if (profile.invoicing_frequency === "DAILY") {
    if (!profile.payment_profile) {
      return false;
    }

    if (
      !profile.address.address_line_1 ||
      !profile.address.locality ||
      !profile.address.postal_code
    ) {
      return false;
    }
  }

  if (isMissingCardOnFile(profile, cardOnFileFlag)) {
    return false;
  }

  return true;
}

export function isMissingCardOnFile(
  profile?: Schemas["CustomerGroupBillingProfile"],
  flagEnabled = false
): boolean {
  if (!flagEnabled) return false;
  return (
    profile?.payer_option === "CLIENT" &&
    profile?.payment_method === "CREDIT_CARD" &&
    profile?.collection_method === "AUTOMATIC" &&
    !profile?.payment_profile
  );
}

export function OrderReviewSummaryBox(props: OrderReviewSummaryBoxProps) {
  const {
    enableAddressBasedInternationalCriminalRecordCheckOrdering,
    enableAddressBasedBankruptcyCheckOrdering,
    enableAddressBasedDirectorshipCheckOrdering,
    enableAddressBasedCreditCheckOrdering,
    enableCivilLitigationRecordCheck,
    billingRequireCardOnFileForOrder,
  } = useFlags();

  const isAddressBasedEnabledForCheck = (checkKey: string) =>
    (checkKey === "CRIMINAL_RECORD_REPORT_1" &&
      enableAddressBasedInternationalCriminalRecordCheckOrdering) ||
    (checkKey === "BANKRUPTCY_RECORD_CHECK_1" &&
      enableAddressBasedBankruptcyCheckOrdering) ||
    (checkKey === "DIRECTORSHIP_RECORD_CHECK_1" &&
      enableAddressBasedDirectorshipCheckOrdering) ||
    (checkKey === "CREDIT_REPORT_1" && enableAddressBasedCreditCheckOrdering) ||
    (checkKey === "CIVIL_LITIGATION_RECORD_CHECK_1" &&
      enableCivilLitigationRecordCheck);

  const {
    order,
    withoutPriceInfo = false,
    hideBorder = false,
    border,
    region,
    isQuickscreen = false,
  } = props;
  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const { data: profile } = useOrderBillingProfile();
  const recipientCount: number = order.recipients.length;
  const costPerRecipient = getOrderCost(
    order.checks,
    order.bundles,
    order.packageData?.price.price
  );
  const isReadyToOrder = checkIfReadyToOrder(
    profile,
    !!billingRequireCardOnFileForOrder
  );
  // Determine currency from package, checks, or bundles
  const currency = order.packageData
    ? order.packageData.price.currency
    : order.checks?.[0]?.currency || order.bundles?.[0]?.currency;

  const subtotal = formatCurrency(
    Number(costPerRecipient) * recipientCount,
    undefined,
    currency,
    2
  );
  const showPriceInfo = !withoutPriceInfo && canViewClientPortalPricing;

  const renderFromPrefix = (check: SelectedChecksType) => {
    if (check.price_is_catalog_minimum) {
      return t("From ");
    }
    if (
      getSelectedUmbrellaOptions(check).some(
        (option) =>
          option.ordering_type === "MULTI_REGION" ||
          option.ordering_type === "SINGLE_REGION" ||
          option.ordering_type === "CURRENT_REGION"
      ) &&
      isAddressBasedEnabledForCheck(check.key)
    ) {
      return t("From ");
    }
    return "";
  };

  // We don't yet have a basket system or the ability to show tax etc.
  // in a future world, the total may vary from subtotal.
  const total = subtotal;
  return (
    <div
      aria-label="order-summary-container"
      className={!withoutPriceInfo ? styles.container : ""}
    >
      <Card
        hideBorder={hideBorder}
        className={!hideBorder ? styles.cardPadding : ""}
        ariaLabel="order-summary"
        type="large"
        border={border}
        testID="order-summary-card"
      >
        <div className={styles.innerContainerFlex}>
          <Text as="h2" style="global/heading-large">
            {t("Order summary")}
          </Text>
          {order.packageData && (
            <SummaryItemWithChecks
              key={order.packageData.id}
              checks={order.packageData.checks.map(
                ({ id, name, selected_options }) => ({
                  id,
                  name,
                  selectedOptionNames:
                    selected_options && selected_options.length > 1
                      ? selected_options
                          .map((opt: any) => opt?.label)
                          .filter((label): label is string => Boolean(label))
                      : undefined,
                  selectedOptionName:
                    selected_options && selected_options.length === 1
                      ? ((selected_options[0] as Record<string, unknown>)
                          .label as string)
                      : undefined,
                })
              )}
              currency={order.packageData.price.currency}
              name={order.packageData.name}
              price={showPriceInfo ? order.packageData.price.price : undefined}
              title={t("PACKAGE")}
            />
          )}
          {order.bundles.map((bundle) => (
            <SummaryItemWithChecks
              currency={bundle.currency}
              key={bundle.id}
              checks={bundle.products.map(({ id, name }) => ({
                id,
                name,
              }))}
              name={bundle.title}
              price={showPriceInfo ? bundle.total : undefined}
              title={t("BUNDLED DEAL")}
            />
          ))}
          {order.checks.map((check, index) => {
            const formattedCheckCost = formatNullableCurrency(check.cost, {
              currency: check.currency,
              locale: "en-US",
              fallback: null,
            });

            return (
              <div className={styles.row} key={index}>
                <div className={styles.spaceBetween} key={index}>
                  <Text
                    as="div"
                    style="global/heading-small"
                    testID={`summary-${slugify(check.name)}-cart`}
                  >
                    {check.name}
                  </Text>
                  {showPriceInfo && (
                    <Text
                      as="div"
                      style="global/heading-small"
                      testID={`summary-${slugify(check.name)}-price`}
                    >
                      {formattedCheckCost
                        ? `${renderFromPrefix(check)}${formattedCheckCost}`
                        : ""}
                    </Text>
                  )}
                </div>
                {check.selected_options?.map((option, optionIndex) => (
                  <Text
                    key={option?.label || optionIndex}
                    as="div"
                    style="global/metric-detail"
                    color="text-weak"
                  >
                    {option?.label}
                  </Text>
                ))}
              </div>
            );
          })}
          <AddOnOrderSummaryContainer
            regularChecks={order.checks}
            packageChecks={order.packageData?.checks || []}
            canViewClientPortalPricing={canViewClientPortalPricing}
          />
          {showPriceInfo && (
            <>
              {recipientCount > 1 && (
                <>
                  <Divider />
                  <div className={css(styles.spaceBetween, styles.row)}>
                    <Text style="global/heading-small">
                      {t("Cost per recipient ")}({recipientCount})
                    </Text>
                    <Text style="global/heading-small">
                      {formatCurrency(
                        Number(costPerRecipient),
                        undefined,
                        currency,
                        2
                      )}
                    </Text>
                  </div>
                </>
              )}
              <Divider />
              <Stack className={styles.totals} gap="space-2">
                <div className={css(styles.spaceBetween, styles.row)}>
                  <Text style="form/body-secondary" testID="subtotal-label">
                    {t("Subtotal")}
                  </Text>
                  <Text style="form/body-secondary">{subtotal}</Text>
                </div>
                <div className={css(styles.spaceBetween, styles.row)}>
                  <Text style="global/heading-medium" testID="total-label">
                    {t("Total")} {region === "au" && `(${t("ex GST")})`}
                  </Text>
                  <Text style="global/heading-medium">
                    {t("From {{total}}", { total })}
                  </Text>
                </div>
                {profile?.invoicing_frequency === "DAILY" && (
                  <Text style="global/body-secondary" color="text-weak">
                    {profile?.payer_option === "APPLICANT" ? (
                      t("Applicant will be charged once the case completes.")
                    ) : (
                      <Trans>
                        Payments will only be charged when the cases are
                        complete. Additional pass-through fees may apply.{" "}
                        <Link
                          href={PASS_THROUGH_ACCESS_FEES_URL}
                          linkStyle="monochrome"
                          textStyle="global/body-secondary"
                          target="_blank"
                          testID="order--review-pay--pass-through-fees-link"
                        >
                          Learn more
                        </Link>
                        .
                      </Trans>
                    )}
                  </Text>
                )}
              </Stack>
            </>
          )}
          {props.onPlaceOrder && (
            <Stack className={styles.order} gap="space-2">
              <PrimaryButton
                fullWidth
                isDisabled={
                  isQuickscreen
                    ? recipientCount === 0 || !!props.disablePlaceOrder
                    : !isReadyToOrder ||
                      recipientCount === 0 ||
                      !!props.disablePlaceOrder
                }
                onPress={props.onPlaceOrder}
                isLoading={props.isSubmitting}
              >
                {t("Place order")}
              </PrimaryButton>
            </Stack>
          )}
        </div>
      </Card>
    </div>
  );
}

const styles = {
  row: css({
    width: "auto",
  }),
  container: css({ flexBasis: "304px", flexGrow: 1 }),
  cardPadding: css({
    paddingTop: "var(--space-3)",
    paddingBottom: "var(--space-3)",
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
  }),
  innerContainerFlex: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  spaceBetween: css({
    display: "flex",
    justifyContent: "space-between",
  }),
  totals: css({
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
  }),
  order: css({
    paddingBottom: "var(--space-2)",
  }),
};
